import AppContext from 'App/AppContext';
import { IPIconButton, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { SkypeCircleCheckIcon } from 'components/Checklist/CheckListStyles';
import { IPeriodFilterData } from 'components/Filter/PeriodFilter';
import Widget, { getWidgetTypes, IWidgetType, WidgetTypes } from 'models/widget';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  WidgetConfigCustomMyNormCoverage,
  WidgetCustomMyNormCoverageConfig,
} from './Widgets/WidgetCustomMyNormCoverage';
import { WidgetConfigCustomNormCoverage, WidgetCustomNormCoverageConfig } from './Widgets/WidgetCustomNormCoverage';
import { WidgetConfigLibraryContent, WidgetLibraryContentConfig } from './Widgets/WidgetLibraryContent';
import { WidgetConfigLibraryReadResponse, WidgetLibraryReadResponseConfig } from './Widgets/WidgetLibraryReadResponse';
import { WidgetConfigLibraryReadStats, WidgetLibraryReadStatsConfig } from './Widgets/WidgetLibraryReadStats';
import {
  WidgetConfigMicrosoftSecureScores,
  WidgetMicrosoftSecureScoresConfig,
} from './Widgets/WidgetMicrosoftSecureScores';
import { WidgetConfigMyNormCoverage, WidgetMyNormCoverageConfig } from './Widgets/WidgetMyNormCoverage';
import { WidgetConfigMyUpcomingTasks, WidgetMyUpcomingTasksConfig } from './Widgets/WidgetMyUpcomingTasks';
import { WidgetConfigNormCoverage, WidgetNormCoverageConfig } from './Widgets/WidgetNormCoverage';
import { WidgetConfigOpenTasksPerUser, WidgetOpenTasksPerUserConfig } from './Widgets/WidgetOpenTasksPerUser';
import { WidgetConfigRiskOwner, WidgetRiskOwnerConfig } from './Widgets/WidgetRiskOwner';
import { WidgetConfigRiskStatus, WidgetRiskStatusConfig } from './Widgets/WidgetRiskStatus';
import {
  WidgetConfigRiskStatusNotAccepted,
  WidgetRiskStatusNotAcceptedConfig,
} from './Widgets/WidgetRiskStatusNotAccepted';
import { WidgetConfigSingleKPI, WidgetSingleKPIConfig } from './Widgets/WidgetSingleKPI';
import { WidgetConfigSingleKPILastPeriod, WidgetSingleKPILastPeriodConfig } from './Widgets/WidgetSingleKPILastPeriod';
import { WidgetConfigSingleRisk, WidgetSingleRiskConfig } from './Widgets/WidgetSingleRisk';
import { WidgetConfigSingleTask, WidgetSingleTaskConfig } from './Widgets/WidgetSingleTask';
import { WidgetConfigTaskAction, WidgetTaskActionConfig } from './Widgets/WidgetTaskAction';
import {
  WidgetConfigTaskCompletedDeadline,
  WidgetTaskCompletedDeadlineConfig,
} from './Widgets/WidgetTaskCompletedDeadline';
import { WidgetConfigTaskStatus, WidgetTaskStatusConfig } from './Widgets/WidgetTaskStatus';
import { WidgetConfigProjectNextStep, WidgetProjectNextStepConfig } from './Widgets/WidgetProjectNextStep';
import { WidgetConfigTaskTag, WidgetTaskTagConfig } from './Widgets/WidgetTaskTag';
import {
  WidgetConfigTenantControlChanges,
  WidgetTenantControlChangesConfig,
} from './Widgets/WidgetTenantControlChanges';
import { WidgetConfigTenantControlStatus, WidgetTenantControlStatusConfig } from './Widgets/WidgetTenantControlStatus';
import {
  WidgetConfigTenantListApprovalStatus,
  WidgetTenantListApprovalStatusConfig,
} from './Widgets/WidgetTenantListApprovalStatus';
import { WidgetConfigTenantRiskChanges, WidgetTenantRiskChangesConfig } from './Widgets/WidgetTenantRiskChanges';
import { WidgetConfigTenantRiskOwner, WidgetTenantRiskOwnerConfig } from './Widgets/WidgetTenantRiskOwner';
import { WidgetConfigTenantRiskStatus, WidgetTenantRiskStatusConfig } from './Widgets/WidgetTenantRiskStatus';
import { WidgetConfigTenantTaskStatus, WidgetTenantTaskStatusConfig } from './Widgets/WidgetTenantTaskStatus';
import { WidgetConfigTenantUserStats, WidgetTenantUserStatsConfig } from './Widgets/WidgetTenantUserStats';

export interface IWidgetConfigRendererProps {
  editmode: boolean;
  widget: Widget;
  period?: IPeriodFilterData;
  onUpdateConfig: (config: string, isValid: boolean) => void;
}

const WidgetConfigRenderer = (props: IWidgetConfigRendererProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['dashboard', 'translation']);
  const [widgetTypes] = useState<IWidgetType[]>(getWidgetTypes(appContext, t));

  if (!props.widget.getType(widgetTypes)?.hasConfig) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPIconButton iconProps={SkypeCircleCheckIcon} />
        <IPText>
          {props.editmode ? t('dashboard:EditWidgetPanel.NoConfig1') : t('dashboard:AddWidgetPanel.NoConfig1')}
        </IPText>
        <IPText>
          {props.editmode ? t('dashboard:EditWidgetPanel.NoConfig2') : t('dashboard:AddWidgetPanel.NoConfig2')}
        </IPText>
      </IPStack>
    );
  }

  switch (props.widget.widgetType) {
    case WidgetTypes.MyUpcomingTasks:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetMyUpcomingTasksConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), true);
      }

      return <WidgetConfigMyUpcomingTasks {...props} />;
    case WidgetTypes.SingleRisk:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetSingleRiskConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), false);
      }

      return <WidgetConfigSingleRisk {...props} />;
    case WidgetTypes.LibraryContent:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetLibraryContentConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), false);
      }

      return <WidgetConfigLibraryContent {...props} />;
    case WidgetTypes.NormCoverage:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetNormCoverageConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), false);
      }

      return <WidgetConfigNormCoverage {...props} />;
    case WidgetTypes.CustomNormCoverage:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetCustomNormCoverageConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), false);
      }

      return <WidgetConfigCustomNormCoverage {...props} />;
    case WidgetTypes.MyNormCoverage:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetMyNormCoverageConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), false);
      }

      return <WidgetConfigMyNormCoverage {...props} />;
    case WidgetTypes.CustomMyNormCoverage:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetCustomMyNormCoverageConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), false);
      }

      return <WidgetConfigCustomMyNormCoverage {...props} />;
    case WidgetTypes.SingleTask:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetSingleTaskConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), false);
      }

      return <WidgetConfigSingleTask {...props} />;
    case WidgetTypes.MicrosoftSecureScores:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetMicrosoftSecureScoresConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), false);
      }

      return <WidgetConfigMicrosoftSecureScores {...props} />;
    case WidgetTypes.RiskStatus:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetRiskStatusConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), true);
      }

      return <WidgetConfigRiskStatus {...props} />;
    case WidgetTypes.RiskStatusNotAccepted:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetRiskStatusNotAcceptedConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), true);
      }

      return <WidgetConfigRiskStatusNotAccepted {...props} />;

    case WidgetTypes.OpenTasksPerUser:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetOpenTasksPerUserConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), true);
      }

      return <WidgetConfigOpenTasksPerUser {...props} />;

    case WidgetTypes.TaskStatus:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetTaskStatusConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), true);
      }

      return <WidgetConfigTaskStatus {...props} />;

    case WidgetTypes.TaskCompletedDeadline:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetTaskCompletedDeadlineConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), true);
      }

      return <WidgetConfigTaskCompletedDeadline {...props} />;

    case WidgetTypes.TaskTag:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetTaskTagConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), true);
      }

      return <WidgetConfigTaskTag {...props} />;

    case WidgetTypes.SingleKPI:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetSingleKPIConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), false);
      }

      return <WidgetConfigSingleKPI {...props} />;

    case WidgetTypes.SingleKPILastPeriod:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetSingleKPILastPeriodConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), false);
      }

      return <WidgetConfigSingleKPILastPeriod {...props} />;
    case WidgetTypes.LibraryReadResponse:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetLibraryReadResponseConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), false);
      }

      return <WidgetConfigLibraryReadResponse {...props} />;
    case WidgetTypes.LibraryReadStats:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetLibraryReadStatsConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), true);
      }

      return <WidgetConfigLibraryReadStats {...props} />;
    case WidgetTypes.TaskAction:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetTaskActionConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), true);
      }

      return <WidgetConfigTaskAction {...props} />;
    case WidgetTypes.TenantRiskStatusPartner:
    case WidgetTypes.TenantRiskStatusOrgUnit:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetTenantRiskStatusConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), false);
      }

      return <WidgetConfigTenantRiskStatus {...props} />;
    case WidgetTypes.TenantRiskChangesPartner:
    case WidgetTypes.TenantRiskChangesOrgUnit:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetTenantRiskChangesConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), false);
      }

      return <WidgetConfigTenantRiskChanges {...props} />;
    case WidgetTypes.RiskOwner:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetRiskOwnerConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), true);
      }

      return <WidgetConfigRiskOwner {...props} />;
    case WidgetTypes.TenantRiskOwnerPartner:
    case WidgetTypes.TenantRiskOwnerOrgUnit:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetTenantRiskOwnerConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), false);
      }

      return <WidgetConfigTenantRiskOwner {...props} />;
    case WidgetTypes.TenantControlStatusPartner:
    case WidgetTypes.TenantControlStatus:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetTenantControlStatusConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), false);
      }

      return <WidgetConfigTenantControlStatus {...props} />;
    case WidgetTypes.TenantControlChangesPartner:
    case WidgetTypes.TenantControlChangesOrgUnit:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetTenantControlChangesConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), false);
      }

      return <WidgetConfigTenantControlChanges {...props} />;
    case WidgetTypes.TenantListApprovalStatusPartner:
    case WidgetTypes.TenantListApprovalStatusOrgUnit:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetTenantListApprovalStatusConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), false);
      }

      return <WidgetConfigTenantListApprovalStatus {...props} />;
    case WidgetTypes.TenantTaskStatusPartner:
    case WidgetTypes.TenantTaskStatusOrgUnit:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetTenantTaskStatusConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), false);
      }

      return <WidgetConfigTenantTaskStatus {...props} />;
    case WidgetTypes.TenantUserStatsPartner:
    case WidgetTypes.TenantUserStatsOrgUnit:
      //create the default config when its empty
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetTenantUserStatsConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), false);
      }

      return <WidgetConfigTenantUserStats {...props} />;
    case WidgetTypes.ProjectNextStep:
      if (!props.widget.widgetConfig) {
        const defConfig = new WidgetProjectNextStepConfig();
        props.onUpdateConfig(JSON.stringify(defConfig), true);
      }

      return <WidgetConfigProjectNextStep {...props} />;
    default:
      return null;
  }
};

export default WidgetConfigRenderer;
